import React, { Component } from 'react';
import { bindActionCreators } from 'redux';
import qs from 'qs';
import renderRoutes, { RouteConfig } from 'src/helpers/renderRoutes';
import asyncConnect from 'src/helpers/asyncConnect';
import {
  DEFAULT_TARGET_VALUE,
  CHARITY_CTA_MAP,
  COMPLETE_PROFILE_URI,
} from 'config';
import logger from 'src/logging';
import CreateForm from 'src/components/CreateForm/CreateForm';
import {
  createPage,
  createPageStarted,
  categoryChanged,
} from 'src/redux/modules/create';
import { loadCategories } from 'src/redux/modules/categories';
import { enrol } from 'src/redux/modules/ab';
import {
  analyticsClick as analyticsClickActionCreator,
  ClickAnalyticsOptions,
} from 'src/redux/modules/analytics';
import {
  countryToCurrencyMap,
  SupportedCurrency,
} from 'src/helpers/currencyMap';
import { getUserConsents } from 'src/redux/modules/consents/actions';
import { fetchCharityInfo, CharityResponse } from 'src/helpers/api/charity';
import { Dispatch } from 'react-redux';
import { AppState } from 'src/redux/modules';
import { CategoryResource } from 'src/helpers/api/categories';
import { CreatePagePayload } from 'src/helpers/api/pages';
import Chrome from '@justgiving/chrome-react';
import sortBy from 'lodash/sortBy';
import { isLoggedIn } from 'src/helpers/api/iam';
import {
  get as getSelf,
  isLoggedIn as isUserLoggedIn,
} from 'src/helpers/api/self';
import { gotoUrl } from 'src/helpers/url';
import { GotoLogin } from './GotoLogin';

const fetchData = async (dispatch: Dispatch<AppState>) => {
  const loadCategoriesPromise = dispatch(loadCategories());
  const abUsPreLaunchPromise = dispatch(enrol('us_prelaunch'));

  await Promise.all([loadCategoriesPromise, abUsPreLaunchPromise]);
};

function getTargetCurrency(countryCode: Country) {
  const preSelectedCurrency = countryToCurrencyMap[countryCode];

  return (preSelectedCurrency && preSelectedCurrency.code) || 'GBP';
}

function getSupportedCurrencies(countries: Country[]) {
  return sortBy(
    countries.map(countryCode => ({
      country: countryCode,
      currency: countryToCurrencyMap[countryCode],
    })),
    ['country']
  );
}

interface StateProps {
  errorReason: string | null;
  initialValues: Partial<CreatePagePayload>;
  submitOnMount: boolean;
  categories: CategoryResource[];
  supportedCurrencies: SupportedCurrency[];
}

interface OwnProps {
  location: any;
  route: RouteConfig;
}

function mapStateToProps(state: AppState, ownProps: OwnProps): StateProps {
  let initialValues = {
    targetAmount: DEFAULT_TARGET_VALUE,
    targetCurrency: getTargetCurrency(state.location.countryCode),
  };

  let submitOnMount = false;

  if (__CLIENT__ && ownProps.location.query && ownProps.location.query.data) {
    try {
      initialValues = JSON.parse(atob(ownProps.location.query.data));
      submitOnMount = true;
    } catch (err) {
      logger.error(
        err,
        'Failed to deserialize create form data from query string.'
      );
    }
  }

  const enabledCountries: Country[] =
    state.ab.us_prelaunch === 'B' ? ['GB', 'US'] : ['GB'];

  return {
    initialValues,
    submitOnMount,
    categories: state.categories,
    errorReason: state.create.hasError ? state.create.errorMessage : null,
    supportedCurrencies: getSupportedCurrencies(enabledCountries),
  };
}

interface DispatchProps {
  createPage: (page: CreatePagePayload, isCharityEndorsed: boolean) => void;
  getUserConsents: () => void;
  createPageStarted: () => void;
  onCategoryChanged: (options: { categoryName: string }) => void;
  analyticsClick: (analyticsOptions: ClickAnalyticsOptions) => void;
}

function mapDispatchToProps(dispatch: Dispatch<AppState>): DispatchProps {
  return bindActionCreators(
    {
      createPage,
      getUserConsents,
      createPageStarted,
      onCategoryChanged: categoryChanged,
      analyticsClick: analyticsClickActionCreator,
    },
    dispatch
  );
}

type Props = StateProps & DispatchProps & OwnProps;

interface State {
  charity: CharityResponse | null;
}

class CreateView extends Component<Props, State> {
  state = {
    charity: null,
  };

  fetchCharityInfo = async (charityId: number) => {
    try {
      const charityInfo = await fetchCharityInfo(charityId);

      this.setState({
        charity: {
          ...charityInfo,
        },
      });
    } catch {
      this.setState({
        charity: null,
      });
    }
  };

  async componentDidMount() {
    this.toggleChromeStartFundraisingButton(false);

    const { submitOnMount, initialValues } = this.props;
    const { charityId } = qs.parse(this.props.location.search.substr(1));

    if (__CLIENT__ && submitOnMount) {
      this.handleSubmit(initialValues as CreatePagePayload);
    }

    // Redirect to /account/completeprofile for users without a given_name or family_name

    const user = await getSelf();

    if (
      __CLIENT__ &&
      isLoggedIn() &&
      isUserLoggedIn(user) &&
      (!user.firstName || !user.lastName)
    ) {
      gotoUrl(
        `${COMPLETE_PROFILE_URI}?returnUrl=${encodeURI(window.location.href)}`
      );
      return;
    }

    this.props.createPageStarted();

    this.props.getUserConsents();

    const whiteListedCharity = CHARITY_CTA_MAP[charityId] !== undefined;

    if (charityId && whiteListedCharity) {
      this.fetchCharityInfo(charityId);
    }
  }

  componentWillUnmount() {
    this.toggleChromeStartFundraisingButton(true);
  }

  handleSubmit = (payload: CreatePagePayload) =>
    this.props.createPage(payload, this.state.charity !== null);

  toggleChromeStartFundraisingButton(toggle: boolean) {
    // TODO: clean this up once the old Chrome gets removed - all
    // we need to do then is to call refresh on unmount
    if (window.jg && window.jg.chrome) {
      if ((window.jg.chrome as any).toggleStartFundraisingButton) {
        (window.jg.chrome as any).toggleStartFundraisingButton(toggle);
      } else if (toggle && window.jg.chrome.refresh) {
        window.jg.chrome.refresh();
      }
    }
  }

  render() {
    const {
      route,
      initialValues,
      categories,
      onCategoryChanged,
      analyticsClick,
    } = this.props;

    if (__CLIENT__ && !isLoggedIn()) {
      return <GotoLogin />;
    }
    return (
      <>
        <Chrome headerVariant="consumer-flow" footerVariant="slim" />

        <CreateForm
          onSubmit={this.handleSubmit}
          initialValues={initialValues}
          categories={categories}
          onCategoryChanged={onCategoryChanged}
          errorReason={this.props.errorReason}
          analyticsClick={analyticsClick}
          charity={this.state.charity}
          supportedCurrencies={this.props.supportedCurrencies}
        />
        {route && renderRoutes(route.routes)}
      </>
    );
  }
}

export default asyncConnect(CreateView, {
  mapStateToProps,
  mapDispatchToProps,
  fetchData,
  id: 'CREATE_VIEW',
  disableLoadingScreen: true,
});



// WEBPACK FOOTER //
// ./src/containers/CreateView.tsx